/**
 * Wi-Fi mesh view helpers -- PURE, so they are unit-tested (`npm run test:unit`) and shared by
 * routes/Mesh.tsx. No React, no fetch.
 *
 * Status colours follow the design rule: the healthy/warn/critical/info ramp is reserved for
 * operational state. A wired ROOT is healthy; a POINT is info (working as designed, but on
 * wireless backhaul); an ISOLATED AP is critical (no cable and no mesh neighbour -- it is about
 * to drop off the controller); off/unknown are neutral.
 */
import type {
  MeshAction, MeshApStatus, MeshDiffChange, MeshOptionAp, MeshOverride, MeshReadinessCounts,
  MeshReadinessStatus, MeshRole, MeshRun, MeshVerdict, SiteMesh,
} from "@/lib/api/siteMesh";

/** StatusPill status values (components/ui/StatusPill). */
export type Tone = "healthy" | "warn" | "critical" | "info" | "unknown";

export const ROLE_LABEL: Record<MeshRole, string> = {
  root: "Wired root",
  point: "Mesh point",
  isolated: "Isolated",
  off: "Mesh off",
  unknown: "Unknown",
};

export function roleTone(role: MeshRole | null | undefined): Tone {
  switch (role) {
    case "root":
      return "healthy";
    case "point":
      return "info";
    case "isolated":
      return "critical";
    default:
      return "unknown";
  }
}

export function qualityTone(q: MeshApStatus["quality"]): Tone {
  if (q === "good") return "healthy";
  if (q === "fair") return "warn";
  if (q === "poor") return "critical";
  return "unknown";
}

export const VERDICT_LABEL: Record<MeshVerdict, string> = {
  supported: "Supported",
  unknown: "Needs package check",
  unsupported: "Not supported",
};

export function verdictTone(v: MeshVerdict, allowed: boolean): Tone {
  if (v === "supported" || allowed) return "healthy";
  return v === "unknown" ? "warn" : "critical";
}

export const ACTION_LABEL: Record<MeshAction, string> = {
  join: "Join mesh",
  update: "Update mesh",
  leave: "Remove mesh",
  none: "No change",
};

/** "-61 dBm" / "—". */
export function fmtRssi(rssi: number | null | undefined): string {
  return typeof rssi === "number" ? `${Math.round(rssi)} dBm` : "—";
}

/** Hop count as shown: root 0 = "wired", null = "—". */
export function fmtHops(hops: number | null | undefined): string {
  if (hops === null || hops === undefined) return "—";
  return hops === 0 ? "wired" : String(hops);
}

/** One AP's preview diff in the shared DiffView shape (`masked` -> `secret`, never revealed). */
export function toConfigDiff(name: string, changes: MeshDiffChange[]) {
  const n = changes.length;
  return {
    summary: n === 0 ? `${name}: no change` : `${name}: ${n} change${n === 1 ? "" : "s"}`,
    changes: changes.map((c) => ({
      op: c.op,
      path: c.path,
      before: c.before,
      after: c.after,
      secret: c.masked,
    })),
  };
}

/** "2 of 3 applied" style progress line for a rollout run. */
export function runProgress(run: MeshRun | null | undefined): string {
  if (!run) return "";
  const total = run.devices.length;
  const applied = run.devices.filter((d) => d.status === "applied").length;
  const failed = run.devices.find((d) => d.status === "failed");
  if (run.status === "running") {
    const cur = run.devices.find((d) => d.status === "running");
    return `${applied} of ${total} applied${cur ? ` — pushing ${cur.name}` : ""}`;
  }
  if (run.status === "succeeded") return `${applied} of ${total} applied`;
  return `${applied} of ${total} applied — stopped at ${failed?.name ?? "an AP"}`;
}

export function runTone(run: MeshRun | null | undefined): Tone {
  if (!run) return "unknown";
  if (run.status === "succeeded") return "healthy";
  if (run.status === "running") return "info";
  return "critical";
}

// Bonded 5 GHz blocks (same table as services/mesh_render._BLOCKS). A 40/80 MHz channel uses
// the whole block, so every channel in it must be allowed.
const BLOCKS: Record<number, number[][]> = {
  40: [[36, 40], [44, 48], [149, 153], [157, 161]],
  80: [[36, 40, 44, 48], [149, 153, 157, 161]],
};

/** The channels from `allowed` that are valid primaries at `width` (whole block allowed). */
export function channelsForWidth(allowed: number[], width: number): number[] {
  if (width === 20) return [...allowed];
  const blocks = BLOCKS[width] ?? [];
  const ok = new Set(allowed);
  return allowed.filter((ch) => {
    const block = blocks.find((b) => b.includes(ch));
    return !!block && block.every((c) => ok.has(c));
  });
}

/** True when `serials` holds at least two eligible APs (the server's min_members rule). */
export function enoughEligible(eligibleCount: number): boolean {
  return eligibleCount >= 2;
}

/* ---- readiness (mesh eligibility deadlock fix) ------------------------------------------ */

/** Short pill text per readiness status (the row's `summary` carries the sentence). */
export const READINESS_LABEL: Record<MeshReadinessStatus, string> = {
  ready: "Ready",
  needs_check: "Needs verification",
  check_failed: "Check failed",
  check_stale: "Re-check needed",
  unsupported: "Not supported",
  not_managed: "Not managed",
  excluded: "Left out",
};

export function readinessTone(status: MeshReadinessStatus): Tone {
  switch (status) {
    case "ready":
      return "healthy";
    case "needs_check":
    case "check_stale":
      return "warn";
    case "check_failed":
    case "unsupported":
      return "critical";
    default:
      return "unknown";
  }
}

/** "Mesh readiness — Main: 0 of 7 ready · 6 need verification · 1 not supported". */
export function readinessLine(group: string, r: MeshReadinessCounts): string {
  const parts = [`${r.ready} of ${r.total} ready`];
  if (r.needs_verification) parts.push(`${r.needs_verification} need${r.needs_verification === 1 ? "s" : ""} verification`);
  if (r.unsupported) parts.push(`${r.unsupported} not supported`);
  if (r.other) parts.push(`${r.other} not managed by Meridian`);
  return `Mesh readiness — ${group}: ${parts.join(" · ")}`;
}

/** The APs a create would leave out (the server's rule: every non-ready AP). */
export function leftOutAps(groupAps: MeshOptionAp[]): MeshOptionAp[] {
  return groupAps.filter((ap) => !ap.eligible);
}

/**
 * The key an "I understand these APs are left out" tick is bound to: the group AND the exact
 * left-out set. The page stores the key that was ticked and treats the tick as valid only while
 * it still equals the current key -- so when a capability check moves an AP to Ready (the set
 * shrinks), or the group changes, the tick is void in the SAME render. (A useEffect reset left
 * one render where Create was enabled for a set the operator had not seen.)
 */
export function leftOutAckKey(group: string, leftOut: Pick<MeshOptionAp, "serial">[]): string {
  return `${group}|${leftOut.map((ap) => ap.serial).sort().join(",")}`;
}

/* ---- Create wording (OP#1802 vocabulary) -------------------------------------------------- */

/**
 * The create button + its helper. The old label "Create mesh (disabled)" read as if the BUTTON
 * were disabled (owner, Jedi Home 2026-10-13). OP#1802 fixes the three verbs: Create = saves a
 * switched-off draft (no device change), Enable = activates it (still no push), Apply = pushes
 * (~4 min outage per AP). The state goes in the helper text, not in the button label.
 */
export const CREATE_MESH_LABEL = "Create mesh";
export const CREATE_MESH_HELP =
  "Saves the mesh as a draft, switched off. Nothing is sent to any AP until you enable it, preview the change and apply it.";

/* ---- AP identity (Jedi Home 2026-10-13) -------------------------------------------------- */

/**
 * The AP's display name, or null when it has none. The controller sends `name` =
 * hostname or, when the AP has no hostname, the SERIAL (mesh_manager._name). The page then
 * showed "a47d78ca5d1e" next to "Edgecore OAP101" (a hostname that happens to be the model),
 * so one row looked like it had a serial for a name and the other a model. A name equal to the
 * serial is treated as "no name"; a real hostname is shown as given.
 */
export function apName(ap: Pick<MeshOptionAp, "serial" | "name">): string | null {
  const name = (ap.name ?? "").trim();
  if (!name || name.toLowerCase() === (ap.serial ?? "").toLowerCase()) return null;
  return name;
}

/** "<name or 'Unnamed AP'> · <serial>" -- the one AP label used everywhere on the Mesh page. */
export function apLabel(ap: Pick<MeshOptionAp, "serial" | "name">): string {
  return `${apName(ap) ?? "Unnamed AP"} · ${ap.serial}`;
}

/* ---- an EXISTING mesh: members, left-out APs, "Add to mesh" ------------------------------ */

/** One AP of an existing mesh's group/member list, as the "AP readiness" section shows it. */
export interface MeshMemberRow {
  ap: MeshOptionAp;
  /** Left out of the mesh: its per-AP override is "exclude" (a create writes that for every
   *  AP it left out -- services/mesh_manager.create_mesh_with_report). */
  leftOut: boolean;
  /** Left out AND ready on its own (options rows are gated WITHOUT overrides), so removing the
   *  "exclude" override is accepted by the server's update gate (_refuse_unsupported). */
  canAdd: boolean;
}

/**
 * The APs an existing mesh covers (its AP group, or its explicit member list) with their
 * membership. Jedi Home 2026-10-13: once Mesh-1 existed the page showed no readiness and no
 * check buttons at all, so the two left-out OAP101s could never be verified or added back.
 */
export function meshMemberRows(
  mesh: Pick<SiteMesh, "group" | "members" | "role_overrides">,
  aps: MeshOptionAp[],
): MeshMemberRow[] {
  const members = new Set(mesh.members ?? []);
  const inScope = mesh.group ? aps.filter((ap) => ap.groups.includes(mesh.group as string)) : aps.filter((ap) => members.has(ap.serial));
  return inScope.map((ap) => {
    const leftOut = (mesh.role_overrides ?? {})[ap.serial] === "exclude";
    return { ap, leftOut, canAdd: leftOut && ap.eligible };
  });
}

/** "Mesh-1: 3 APs in the mesh · 2 left out (1 ready to add · 1 needs verification)". */
export function membershipLine(name: string, rows: MeshMemberRow[]): string {
  const inMesh = rows.filter((r) => !r.leftOut).length;
  const out = rows.filter((r) => r.leftOut);
  let line = `${name}: ${inMesh} AP${inMesh === 1 ? "" : "s"} in the mesh`;
  if (out.length) {
    const ready = out.filter((r) => r.canAdd).length;
    const verify = out.filter((r) => !r.canAdd && (r.ap.check_available ?? r.ap.can_check)).length;
    const parts = [];
    if (ready) parts.push(`${ready} ready to add`);
    if (verify) parts.push(`${verify} need${verify === 1 ? "s" : ""} verification`);
    line += ` · ${out.length} left out${parts.length ? ` (${parts.join(" · ")})` : ""}`;
  }
  return line;
}

/** The role_overrides map an "Add to mesh" PATCH sends: the AP's "exclude" removed (it becomes
 *  "auto": wired = root, no cable = mesh point). The PATCH replaces the whole map, so every
 *  other override is kept as is. */
export function overridesAfterAdd(
  overrides: Record<string, MeshOverride> | null | undefined,
  serial: string,
): Record<string, MeshOverride> {
  const out: Record<string, MeshOverride> = { ...(overrides ?? {}) };
  delete out[serial];
  return out;
}

/** What "Add to mesh" does and does not do (shown in its preview before the save). */
export const ADD_TO_MESH_NOTE =
  "This saves the mesh's settings only. Nothing is sent to any AP. Next, Preview below shows this AP's config change, and Apply pushes it (the AP is offline about 4 minutes).";

/** Create is allowed with >= 2 ready APs, and -- when any AP will be left out -- only after the
 *  operator ticked the acknowledgement. Mirrors the server (422 min_members / exclusions_need_ack). */
export function canCreateMesh(readyCount: number, leftOutCount: number, acknowledged: boolean): boolean {
  return enoughEligible(readyCount) && (leftOutCount === 0 || acknowledged);
}

/** "Passed (Meridian check) by alice, 12 Oct 14:02" style line for a recorded check. */
export function checkLine(check: MeshOptionAp["check"]): string {
  if (!check) return "No capability check recorded.";
  const verdict =
    check.state === "stale" ? "Recorded for older firmware" : check.result === "pass" ? "Passed" : "Failed";
  const how = check.method === "auto" ? "Meridian read-only check" : "recorded by hand";
  const when = check.checked_at ? ` at ${check.checked_at.replace("T", " ").slice(0, 16)} UTC` : "";
  return `${verdict} (${how}) by ${check.checked_by ?? "unknown"}${when}.`;
}

/**
 * Whether a readiness row says "needs a check" and whether THIS viewer gets the Run/Record
 * buttons. The server computes `can_check` per caller (only MESH_WRITE_ROLE / org_admin may run
 * the check; Jedi Home 2026-10-13: site operators were offered a button that could only 403)
 * and `check_available` regardless of role. An older controller sent only `can_check`, which
 * then meant "available"; the button is still role-gated in the page.
 */
export function meshCheckOffer(
  ap: Pick<MeshOptionAp, "eligible" | "can_check" | "check_available">,
): { needsCheck: boolean; mayRun: boolean } {
  const available = ap.check_available ?? ap.can_check;
  const needsCheck = !!available && !ap.eligible;
  return { needsCheck, mayRun: needsCheck && !!ap.can_check };
}
